import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { translations } from "../lib/i18n";
import phrases from "../lib/phrases.json";
import api from "../lib/api";

const LangContext = createContext(null);
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Gettext-style helper for interface labels: key = French source string.
let _lang = localStorage.getItem("club_lang") || "fr";
export function tr(s) {
  if (_lang === "fr" || !s) return s;
  const p = phrases[s] || phrases[String(s).replace(/\u2019/g, "'")];
  return (p && p[_lang]) || s;
}

function setPath(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof cur[parts[i]] !== "object" || cur[parts[i]] === null) cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem("club_lang") || "fr";
  });
  const [content, setContent] = useState({});
  const [preview, setPreview] = useState(null);

  // CMS live preview: when loaded inside the editor iframe (?cmsPreview=1),
  // accept draft content from the parent window and overlay it.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("cmsPreview")) return;
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "rycm-preview" && e.data.pages) setPreview(e.data.pages);
    };
    window.addEventListener("message", onMsg);
    try { window.parent?.postMessage({ type: "rycm-preview-ready" }, window.location.origin); } catch { /* noop */ }
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const refreshContent = useCallback(() => {
    api.get("/content").then((r) => setContent(r.data?.pages || {})).catch(() => {});
  }, []);

  useEffect(() => { refreshContent(); }, [refreshContent]);

  _lang = lang;
  useEffect(() => {
    localStorage.setItem("club_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const effectiveContent = useMemo(() => (preview ? { ...content, ...preview } : content), [content, preview]);

  const t = useMemo(() => {
    const base = JSON.parse(JSON.stringify(translations[lang] || translations.fr));
    Object.values(effectiveContent).forEach((p) => {
      const flat = (p.texts || {})[lang] || {};
      Object.entries(flat).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) setPath(base, k, v);
      });
    });
    return base;
  }, [lang, effectiveContent]);

  const img = useCallback((page, key, def) => {
    const u = effectiveContent[page]?.images?.[key];
    if (!u) return def;
    return u.startsWith("/uploads/") ? `${BACKEND}/api${u}` : u;
  }, [effectiveContent]);

  const visible = useCallback((page, section) => effectiveContent[page]?.sections?.[section] !== false, [effectiveContent]);
  const seo = useCallback((page) => effectiveContent[page]?.seo || {}, [effectiveContent]);

  const sectionOrder = useCallback((page, id, defaults) => {
    const saved = effectiveContent[page]?.options?.section_order;
    if (!saved || !saved.length) return defaults.indexOf(id) + 1;
    const list = [...saved.filter((x) => defaults.includes(x)), ...defaults.filter((x) => !saved.includes(x))];
    return list.indexOf(id) + 1;
  }, [effectiveContent]);

  return (
    <LangContext.Provider value={{ lang, setLang, t, content, img, visible, seo, sectionOrder, refreshContent }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
